import { CtaConfig, ServerConfig, WidgetEnv } from "@xapp/stentor-chat-widget";
import { createContext, useContext, useMemo } from "react";
import { useSelector } from "react-redux";
import { ChatState } from "../store/ChatState";
import { log } from "../utils";

export function getBasePathFromWidgetSource(): { path: string; key?: string } {
    // the ID "xapp-js" is on the injected <script id="xapp-js" src="..." ></script>
    const xappScriptNode = document?.getElementById("xapp-js") as HTMLScriptElement | undefined;
    const path = getBasePath(xappScriptNode?.src);
    return path;
}

export const defaultServerUrl = process.env.SERVER_URL;
export const defaultAccountKey = process.env.ACCOUNT_KEY;
export const defaultWidgetButtonWidth = "48";
export const defaultMobileWidgetButtonWidth = "30";
export const defaultNonMobileScreenWidth = "400";

export const scheduleWidgetUrl = "ScheduleButton";

// TODO: Review these
export interface Config {
    readonly connection: ServerConfig;
    readonly assetUrl?: string;
    readonly query?: QueryMap;
    readonly cta?: CtaConfig;
    readonly env?: WidgetEnv;
}

interface QueryMap {
    readonly [key: string]: string;
}

export function getBasePath(path?: string): { path: string; key?: string } {
    if (!path) {
        return { path };
    }
    const lastPathSep = path.lastIndexOf("/");
    let result = "";
    if (lastPathSep >= 0) {
        result = path.substr(0, lastPathSep);
    }

    const url = new URL(path);

    const hostname = url.hostname;
    // This accounts for how NITRO Pack, a wordpress plugin, will cache our JS but not our CSS
    // so we just load it from the prod environment
    if (hostname.endsWith("nitrocdn.com")) {
        result = "https://widget.xapp.ai";
    }

    const params = new URLSearchParams(url.search);
    const key = params.get("key");

    return { path: result, key };
}

export const ChatConfigContext = createContext<Config>(null);

export function useWidgetEnv(): WidgetEnv | undefined {
    const ctx = useContext(ChatConfigContext);
    return ctx?.env;
}

export function useAccountKey(): string | undefined {
    const env = useContext(ChatConfigContext);
    return env?.connection?.accountKey;
}

export function getServerConfig(env?: WidgetEnv): ServerConfig {
    if (env?.connection) {
        return env.connection;
    }
    return {
        serverUrl: env?.serverUrl,
        accountKey: env?.accountKey,
        type: env?.direct ? "direct" : "websocket",
    };
}

export function useServerConfig(env?: WidgetEnv, nonce?: string): ServerConfig {
    const connection = getServerConfig(env);

    const { accountKey, serverUrl, type, timeout } = connection;

    return useMemo<ServerConfig>(
        () => ({
            accountKey: accountKey,
            serverUrl: serverUrl,
            timeout: timeout,
            type: type,
            nonce,
        }),
        [accountKey, serverUrl, timeout, type, nonce]
    );
}

export function useConnectionInfo(env?: WidgetEnv): ServerConfig {
    const nonce = useSelector<ChatState, string | undefined>((state) => state.connection.nonce);
    log(`nonce from Redux: ${nonce}`);
    return useServerConfig(env, nonce);
}
